import React from 'react';
import { motion } from 'framer-motion';
import { Menu, Bell, Wallet, LifeBuoy } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface HeaderProps {
  profile: {
    balance: number;
    name?: string;
  } | null;
  onMenuClick: () => void;
  onNotificationClick: () => void;
  announcementsCount: number;
}

const Header: React.FC<HeaderProps> = ({ 
  profile, 
  onMenuClick, 
  onNotificationClick, 
  announcementsCount 
}) => {
  const navigate = useNavigate();

  const formatINR = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="fixed top-0 w-full bg-gradient-to-r from-[#0a0a0f]/95 via-[#0f0f1a]/95 to-[#0a0a0f]/95 backdrop-blur-xl border-b border-purple-500/20 z-40">
      <div className="px-4 md:px-8 py-3">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <motion.button 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onMenuClick}
              className="md:hidden w-10 h-10 rounded-full bg-gradient-to-br from-purple-500/20 to-blue-500/20 flex items-center justify-center"
            >
              <Menu className="w-5 h-5 text-purple-400" />
            </motion.button>
            
            <div className="text-center">
              <h1 className="text-2xl font-rajdhani font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent tracking-wider">
                ARENA X
              </h1>
              <div className="flex items-center justify-center gap-1 mt-0.5">
                <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                <span className="text-green-500 text-[10px] font-rajdhani font-bold tracking-wider">LIVE</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* SUPPORT BUTTON - DESKTOP ONLY */}
<motion.button
  whileHover={{ scale: 1.05 }}
  whileTap={{ scale: 0.95 }}
  onClick={() => navigate('/support')}
  className="hidden md:flex items-center gap-2 bg-gradient-to-r from-purple-500 to-blue-500 px-4 py-2 rounded-xl hover:shadow-lg hover:shadow-purple-500/20 transition-all"
>
  <LifeBuoy className="w-4 h-4 text-white" />
  <span className="text-white font-rajdhani font-medium text-sm hidden lg:inline">
    Help
  </span>
</motion.button>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onNotificationClick}
              className="relative w-10 h-10 rounded-full bg-gradient-to-br from-purple-500/20 to-blue-500/20 flex items-center justify-center"
            >
              <Bell className="w-5 h-5 text-purple-400" />
              {announcementsCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-white text-[10px] flex items-center justify-center">
                  {announcementsCount}
                </span>
              )}
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/dashboard/wallet')}
              className="flex items-center gap-2 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 px-3 py-2 rounded-xl border border-yellow-500/30 hover:border-yellow-500/50 transition-all"
            >
              <Wallet className="w-5 h-5 text-yellow-500" />
              <span className="text-yellow-500 font-rajdhani font-bold text-sm">
                {formatINR(profile?.balance || 0)}
              </span>
            </motion.button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;